// Back-in-stock subscription for sold-out variants on the product page.
// The cart button switches to its "notify" state (.js-bis-trigger) in buy-buttons.liquid / product-form.js;
// a click opens <back-in-stock-modal> and the e-mail is sent to Klaviyo's client API.
if (!customElements.get('back-in-stock-modal')) {
  // Latest GA revision in Klaviyo's API changelog (2024-10-15 was retired on 2026-10-15).
  const KLAVIYO_REVISION = '2026-07-15';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const SUCCESS_CLOSE_DELAY = 2500;

  const getLanguage = () => {
    try {
      return (window.Weglot && window.Weglot.getCurrentLang()) || 'de';
    } catch (e) {
      return 'de';
    }
  };

  const trackClarity = (eventName) => {
    if (typeof window.clarity === 'function') window.clarity('event', eventName);
  };

  customElements.define(
    'back-in-stock-modal',
    class BackInStockModal extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.dialog = this.querySelector('dialog');
        this.form = this.querySelector('[data-bis-form]');
        this.input = this.form.querySelector('input[type="email"]');
        this.submitButton = this.form.querySelector('[type="submit"]');
        this.spinner = this.submitButton.querySelector('.loading__spinner');
        this.successMessage = this.querySelector('[data-bis-success]');
        this.variantLabel = this.querySelector('[data-bis-variant]');

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.querySelector('[data-bis-close]').addEventListener('click', () => this.close());
        // The dialog has no padding, so a click whose target is the dialog itself hit the backdrop.
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
        this.dialog.addEventListener('close', this.onClose.bind(this));
        // Theme modals (e.g. quick add) close on Escape *keyup*; keep the keyup of the Escape that closed this
        // dialog from also closing the modal underneath.
        this.dialog.addEventListener('cancel', () => {
          window.addEventListener(
            'keyup',
            (event) => {
              if (event.key === 'Escape') event.stopPropagation();
            },
            { capture: true, once: true }
          );
        });
      }

      open(trigger) {
        this.trigger = trigger;
        this.variantId = trigger.dataset.variantId;
        const variantTitle = trigger.dataset.variantTitle || '';
        this.variantLabel.textContent = variantTitle === 'Default Title' ? '' : variantTitle;
        this.variantLabel.hidden = !this.variantLabel.textContent;

        this.form.hidden = false;
        this.successMessage.hidden = true;
        this.showError(null);

        this.dialog.showModal();
        document.documentElement.classList.add('bis-modal-open');
        this.input.focus();
        trackClarity('BIS_Open');
      }

      close() {
        if (this.dialog.open) this.dialog.close();
      }

      onClose() {
        clearTimeout(this.closeTimer);
        document.documentElement.classList.remove('bis-modal-open');
        // The button may have been re-rendered by a variant change while the modal was open.
        const productInfo = this.closest('product-info');
        const focusTarget = this.trigger?.isConnected
          ? this.trigger
          : productInfo?.querySelector(`#ProductSubmitButton-${this.dataset.sectionId}`);
        focusTarget?.focus();
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.busy) return;

        const email = this.input.value.trim();
        if (!EMAIL_PATTERN.test(email)) {
          this.showError('invalid');
          this.input.focus();
          return;
        }
        this.showError(null);
        this.setBusy(true);

        try {
          const response = await fetch(
            `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${this.dataset.companyId}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
              body: JSON.stringify(this.buildPayload(email)),
            }
          );
          if (response.status !== 202) throw new Error(`Klaviyo responded with ${response.status}`);
          this.onSuccess(email);
        } catch (error) {
          console.warn('[back-in-stock]', error);
          this.showError('general');
        } finally {
          this.setBusy(false);
        }
      }

      buildPayload(email) {
        return {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.variantId}` } },
            },
          },
        };
      }

      onSuccess(email) {
        this.identify(email);
        trackClarity('BIS_Submit');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'back_in_stock_submit',
          variant_id: Number(this.variantId),
          product_title: this.dataset.productTitle,
        });

        this.form.hidden = true;
        this.successMessage.hidden = false;
        this.closeTimer = setTimeout(() => this.close(), SUCCESS_CLOSE_DELAY);
      }

      // Sets the "lang" profile property the Back in Stock flow branches on.
      identify(email) {
        const klaviyo = window.klaviyo;
        if (!klaviyo) return;
        const properties = { email, lang: getLanguage() };
        try {
          if (typeof klaviyo.identify === 'function') {
            Promise.resolve(klaviyo.identify(properties)).catch((error) => console.warn('[back-in-stock]', error));
          } else if (typeof klaviyo.push === 'function') {
            klaviyo.push(['identify', properties]);
          }
        } catch (error) {
          console.warn('[back-in-stock]', error);
        }
      }

      showError(type) {
        this.querySelectorAll('[data-bis-error]').forEach((element) => {
          element.hidden = element.dataset.bisError !== type;
        });
        this.input.setAttribute('aria-invalid', type === 'invalid');
      }

      setBusy(busy) {
        this.busy = busy;
        this.submitButton.disabled = busy;
        this.submitButton.classList.toggle('loading', busy);
        this.submitButton.setAttribute('aria-busy', busy);
        this.spinner?.classList.toggle('hidden', !busy);
      }
    }
  );

  // Delegated, so it keeps working after product-info swaps the buy buttons on variant changes.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.js-bis-trigger');
    if (!trigger) return;
    event.preventDefault();
    const modal = (trigger.closest('product-info') || document).querySelector('back-in-stock-modal');
    modal?.open(trigger);
  });
}
